import React from 'react';
import './Button.scss';
import { Button as Btn, ButtonProps } from '@mui/material';
import { FFLoader } from '../flyfishIcons/icons';
import styled from 'styled-components';

type Props = {
	className?: string;
	size?: 'small' | 'large';
	buttonType?: 'dark' | 'light' | 'transparent';
	loading?: boolean;
	fullWidth?: boolean;
	startIcon?: React.ReactNode;
	disabled?: boolean;
	variant?: string;
} & ButtonProps;

const BUTTON = styled.div`
	&.custom-button {
		.MuiButtonBase-root.transparent {
			&:not(.Mui-disabled):hover {
				&:after {
					background-color: rgba(255, 255, 255, 0.2);
				}
			}
			&:not(.Mui-disabled):focus,
			&:not(.Mui-disabled):active {
				&:after {
					background-color: rgba(255, 255, 255, 0.3);
				}
			}
		}
		.MuiButton-root {
			position: relative;
			border-radius: 8px;
			text-transform: none;

			&.dark {
				background-color: var(--violet-600);
			}

			&.light {
				color: var(--violet-600);
				background-color: var(--violet-100);
			}

			&.transparent {
				color: white;
				background-color: transparent;
				border: 1px solid var(--white);
			}

			@mixin button {
				font-family: var(--fontFamily);
				font-weight: 600;
				line-height: 1.5;
			}

			&.button1 {
				@include button;
				font-size: 16px;
				letter-spacing: 0.48px;
			}

			&.button2 {
				@include button;
				font-size: 14px;
				letter-spacing: 0.56px;
			}

			.custom-button-body {
				white-space: nowrap;
				word-break: keep-all;
				&.dark {
					color: white;
				}
				&.light {
					color: var(--violet-600);
				}
				&.transparent {
					color: white;
				}
			}

			&.MuiButton-sizeLarge {
				min-width: 72px;
				height: 34px;
				padding: 7px 16px;
			}

			&.MuiButton-sizeSmall {
				min-width: 60px;
				height: 30px;
				padding: 6px 12px 6px;
			}

			&.icon {
				&.MuiButton-sizeLarge {
					padding: 5px 16px 5px 8px;
				}

				&.MuiButton-sizeSmall {
					padding: 5px 12px 5px 6px;
				}

				.MuiButton-startIcon {
					margin-left: 0;

					&.MuiButton-iconSizeLarge {
						margin-right: 4px;

						svg {
							font-size: 24px;
						}
					}
				}
				.MuiButton-startIcon {
					&.MuiButton-iconSizeSmall {
						margin-right: 2px;

						svg {
							font-size: 20px;
						}
					}
				}
			}

			&:not(.icon).loading {
				.custom-button-body {
					visibility: hidden;
				}
			}

			.button-loader {
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%);
			}

			&.Mui-disabled {
				pointer-events: auto;
				cursor: not-allowed;
				opacity: 0.6;

				&.light {
					color: var(--violet-600);
					background-color: var(--violet-100);
				}

				&.dark {
					background-color: var(--violet-600);
					color: var(--white);
				}
				&.transparent {
					color: white;
					background-color: transparent;
					border: 1px solid var(--white);
				}
			}

			&:after {
				position: absolute;
				content: '';
				left: 0;
				top: 0;
				width: 100%;
				height: 100%;
				border-radius: 8px;
			}
			&:not(.Mui-disabled):hover {
				&:after {
					background-color: var(--black-10);
					// background-color: rgba(255, 255, 255, 0.3);
				}
			}

			&:not(.Mui-disabled):focus,
			&:not(.Mui-disabled):active {
				&:after {
					background-color: var(--black-20);
				}
			}
		}
	}
`;

const Button = ({
	className,
	children,
	size = 'small',
	buttonType = 'dark',
	loading,
	fullWidth = false,
	startIcon,
	disabled,
	variant,
	...rest
}: Props) => {
	return (
		<BUTTON className='custom-button'>
			<Btn
				fullWidth={fullWidth}
				variant={variant ? variant : 'contained'}
				size={size}
				className={`${size === 'small' ? 'button2' : 'button1'} ${buttonType}${
					startIcon ? ' icon' : ''
				}${loading ? ' loading' : ''} ${className}`}
				disableElevation={true}
				onMouseDown={(event) => event.preventDefault()}
				disableRipple
				disabled={disabled || loading}
				startIcon={
					startIcon ? (
						loading ? (
							<FFLoader
								width={`${size === 'small' ? 20 : 24}`}
								height={`${size === 'small' ? 20 : 24}`}
							/>
						) : (
							startIcon
						)
					) : null
				}
				{...rest}
			>
				{loading && !startIcon && (
					<span className='button-loader'>
						<FFLoader
							width={`${size === 'small' ? 20 : 24}`}
							height={`${size === 'small' ? 20 : 24}`}
						/>
					</span>
				)}
				<div
					className={`custom-button-body ${
						size === 'small' ? 'button2' : 'button1'
					} ${buttonType}`}
				>
					{children}
				</div>
			</Btn>
		</BUTTON>
	);
};

export default Button;
